import { ToastProps, ToastVariant } from '../components/ui/Toast';
import { ConfirmDialogProps } from '../components/ui/ConfirmDialog';

type ToastCallback = (toasts: ToastProps[]) => void;
type ConfirmCallback = (dialog: ConfirmDialogProps | null) => void;

class ToastManager {
  private toasts: ToastProps[] = [];
  private confirmDialog: ConfirmDialogProps | null = null;
  private toastListeners: Set<ToastCallback> = new Set();
  private confirmListeners: Set<ConfirmCallback> = new Set();

  // Subscribe to toast updates
  subscribeToasts(callback: ToastCallback) {
    this.toastListeners.add(callback);
    callback(this.toasts); // Send current state immediately
    return () => this.toastListeners.delete(callback);
  }

  // Subscribe to confirm dialog updates
  subscribeConfirm(callback: ConfirmCallback) {
    this.confirmListeners.add(callback);
    callback(this.confirmDialog); // Send current state immediately
    return () => this.confirmListeners.delete(callback);
  }

  // Show toast notification
  show(variant: ToastVariant, message: string, title?: string, duration = 4000) {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: ToastProps = {
      id,
      message: title ? `${message}: ${title}` : message,
      variant,
      duration,
      onClose: (id: string) => this.remove(id)
    };

    this.toasts = [...this.toasts, newToast];
    this.notifyToastListeners();
  }

  // Remove toast by id
  remove(id: string) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notifyToastListeners();
  }

  // Show confirm dialog
  confirm(
    title: string,
    message: string,
    options?: {
      confirmText?: string;
      cancelText?: string;
      type?: 'danger' | 'warning' | 'info';
      icon?: 'delete' | 'user-remove' | 'logout' | 'warning';
    }
  ): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmDialog = {
        isOpen: true,
        title,
        message,
        confirmText: options?.confirmText,
        cancelText: options?.cancelText,
        variant: options?.type || 'danger',
        icon: options?.icon || 'warning',
        onConfirm: () => {
          this.confirmDialog = null;
          this.notifyConfirmListeners();
          resolve(true);
        },
        onClose: () => {
          this.confirmDialog = null;
          this.notifyConfirmListeners();
          resolve(false);
        }
      };

      this.notifyConfirmListeners();
    });
  }

  private notifyToastListeners() {
    this.toastListeners.forEach(callback => callback([...this.toasts]));
  }

  private notifyConfirmListeners() {
    this.confirmListeners.forEach(callback => callback(this.confirmDialog));
  }
}

export const toastManager = new ToastManager();
